import * as React from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material'
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded'
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { ActionType, ErrorType } from '../types'

const formatter = new Intl.NumberFormat('en-US')

const formatRemaining = milliseconds => {
    if (!Number.isFinite(milliseconds))
        return 'No time limit'
    const seconds = Math.max(0, Math.ceil(milliseconds / 1000))
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const rest = seconds % 60
    if (days > 0)
        return `${days}d ${hours}h`
    if (hours > 0)
        return `${hours}h ${minutes}m`
    return `${minutes}m ${String(rest).padStart(2, '0')}s`
}

const formatPrice = option => {
    if (option?.realMoney)
        return Number.isFinite(Number(option?.euroAmount)) ? `EUR ${Number(option.euroAmount).toFixed(2)}` : 'Real money'
    const costs = Array.isArray(option?.costs) ? option.costs : []
    if (!costs.length)
        return 'Free'
    return costs.map(cost => `${formatter.format(Number(cost?.amount ?? 0))} ${cost?.currency ?? ''}`.trim()).join(' + ')
}

function RewardItem({ reward, t }) {
    return (
        <Tooltip title={`${reward?.label || t('Unknown reward')} · ${formatter.format(Number(reward?.amount ?? 0))}`}>
            <Paper
                variant="outlined"
                sx={{
                    p: 0.65,
                    minWidth: 96,
                    maxWidth: 150,
                    flex: '1 1 105px',
                    borderColor: 'rgba(250,204,21,.22)',
                    background: 'rgba(15,23,42,.58)'
                }}
            >
                <Stack direction="row" spacing={0.65} alignItems="center">
                    {reward?.iconUrl ? (
                        <Box
                            component="img"
                            src={reward.iconUrl}
                            alt=""
                            loading="lazy"
                            sx={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0 }}
                        />
                    ) : (
                        <Inventory2RoundedIcon sx={{ width: 30, height: 30, color: '#facc15', flexShrink: 0 }} />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: '#fef3c7', fontWeight: 900, lineHeight: 1.15 }}>
                            {`×${formatter.format(Number(reward?.amount ?? 0))}`}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.15, overflowWrap: 'anywhere' }}>
                            {t(reward?.label || 'Unknown reward')}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        </Tooltip>
    )
}

export default function OfferOverviewCard({ overview, user, ws, now = Date.now(), t = value => value }) {
    const offers = Array.isArray(overview?.offers) ? overview.offers : []
    const promotions = Array.isArray(overview?.promotions) ? overview.promotions : []
    const rubyOffers = Array.isArray(overview?.rubyOffers) ? overview.rubyOffers : []
    const [requestedKey, setRequestedKey] = React.useState('')
    const [expandedKeys, setExpandedKeys] = React.useState(() => new Set())
    const purchase = overview?.purchase
    const isExpanded = key => expandedKeys.has(key)
    const toggleExpanded = key => setExpandedKeys(previous => {
        const next = new Set(previous)
        if (next.has(key))
            next.delete(key)
        else
            next.add(key)
        return next
    })

    React.useEffect(() => {
        if (!requestedKey)
            return undefined
        if (purchase?.state && `${purchase.offerID}:${Number(purchase.descriptionIndex ?? 0)}` === requestedKey && purchase.state !== 'pending') {
            setRequestedKey('')
            return undefined
        }
        const timer = window.setTimeout(() => setRequestedKey(''), 15000)
        return () => window.clearTimeout(timer)
    }, [purchase, requestedKey])

    const requestPurchase = (offer, option) => {
        if (!ws || Number(ws.readyState) !== 1 || !user?.id || !option?.purchasable || option?.realMoney)
            return
        const contents = (Array.isArray(option.rewards) ? option.rewards : [])
            .slice(0, 6)
            .map(reward => `${formatter.format(Number(reward?.amount ?? 0))} × ${t(reward?.label || 'Unknown reward')}`)
            .join(', ')
        const confirmation = [
            `${t('Buy this offer')}?`,
            `${t('Price')}: ${formatPrice(option)}`,
            `${t('Contents')}: ${contents || t('Details unavailable')}`
        ].join('\n')
        if (!window.confirm(confirmation))
            return

        const key = `${offer.offerID}:${Number(option.descriptionIndex ?? 0)}`
        setRequestedKey(key)
        ws.send(JSON.stringify([
            ErrorType.Success,
            ActionType.PurchasePrivateOffer,
            { ...user, offerID: String(offer.offerID), descriptionIndex: Number(option.descriptionIndex ?? 0) }
        ]))
    }

    const activeOfferCount = offers.reduce((total, offer) => total + (Array.isArray(offer?.options) ? offer.options.length : 0), 0) +
        rubyOffers.reduce((total, offer) => total + Math.max(1, Array.isArray(offer?.options) ? offer.options.length : 0), 0)

    return (
        <Box
            data-testid="offer-overview-card"
            sx={{
                p: 1.25,
                borderRadius: 2.2,
                border: '1px solid rgba(250,204,21,.3)',
                background: 'linear-gradient(145deg,rgba(39,31,18,.82),rgba(17,24,39,.96))'
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <LocalOfferRoundedIcon sx={{ color: '#facc15' }} />
                    <Box>
                        <Typography variant="body2" sx={{ color: '#fef3c7', fontWeight: 950 }}>
                            {t('Offer overview')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('Live offers reported by the game')}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={`${activeOfferCount} ${t('offers')}`} sx={{ color: '#fef3c7' }} />
                    {overview?.updatedAt ? <Chip size="small" variant="outlined" label={formatRemaining(Number(now) - Number(overview.updatedAt) <= 0 ? 0 : Number(now) - Number(overview.updatedAt)) + ` ${t('since update')}`} /> : null}
                </Stack>
            </Stack>

            {purchase?.state === 'failed' ? (
                <Typography variant="caption" sx={{ mt: 0.8, display: 'block', color: '#fca5a5', fontWeight: 800 }}>
                    {`${t('Purchase failed')}: ${purchase.error || t('Unknown server response')}`}
                </Typography>
            ) : purchase?.state === 'succeeded' ? (
                <Typography variant="caption" sx={{ mt: 0.8, display: 'block', color: '#86efac', fontWeight: 800 }}>
                    {t('Offer purchased successfully.')}
                </Typography>
            ) : null}

            {promotions.length > 0 ? (
                <Stack spacing={0.7} sx={{ mt: 1 }}>
                    {promotions.map(promotion => {
                        const key = `promotion:${promotion.key}`
                        const expanded = isExpanded(key)
                        return (
                            <Paper key={key} variant="outlined" sx={{ p: 0.8, borderColor: 'rgba(250,204,21,.24)', background: 'rgba(2,6,23,.28)' }}>
                                <Stack direction="row" spacing={0.7} alignItems="center">
                                    {promotion.iconUrl ? <Box component="img" src={promotion.iconUrl} alt="" sx={{ width: 42, height: 42, objectFit: 'contain' }} /> : <PaymentsRoundedIcon sx={{ color: '#facc15' }} />}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ color: '#fef3c7', fontWeight: 900 }}>{t(promotion.title)}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatRemaining(Number(promotion.expiresAt) - Number(now))}</Typography>
                                    </Box>
                                    <IconButton size="small" aria-label={expanded ? t('Collapse offer') : t('Expand offer')} onClick={() => toggleExpanded(key)}>
                                        <ExpandMoreRoundedIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }} />
                                    </IconButton>
                                </Stack>
                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                    <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
                                        {(promotion.details || []).map(detail => <Chip key={detail} size="small" label={t(detail)} />)}
                                    </Stack>
                                    {!promotion.details?.length ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('The game reports only the active bonus and timer for this offer.')}</Typography> : null}
                                </Collapse>
                            </Paper>
                        )
                    })}
                </Stack>
            ) : null}

            {rubyOffers.length > 0 ? (
                <Stack spacing={0.8} sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#facc15', fontWeight: 950 }}>
                        {t('Ruby offers')}
                    </Typography>
                    {rubyOffers.map(rubyOffer => {
                        const key = `ruby:${rubyOffer.key}`
                        const expanded = isExpanded(key)
                        return (
                        <Paper key={key} variant="outlined" sx={{ p: 1, borderColor: 'rgba(239,68,68,.28)', background: 'rgba(69,10,10,.16)' }}>
                            <Stack direction="row" spacing={0.8} alignItems="center">
                                {rubyOffer.iconUrl ? (
                                    <Box component="img" src={rubyOffer.iconUrl} alt="" sx={{ width: 46, height: 46, objectFit: 'contain', flexShrink: 0 }} />
                                ) : (
                                    <PaymentsRoundedIcon sx={{ width: 40, height: 40, color: '#ef4444', flexShrink: 0 }} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ color: '#fee2e2', fontWeight: 950 }}>
                                        {t(rubyOffer.title || 'Ruby offer')}
                                    </Typography>
                                    <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap" sx={{ mt: 0.35 }}>
                                        {rubyOffer.discount ? <Chip size="small" label={`-${rubyOffer.discount}%`} sx={{ color: '#fecaca' }} /> : null}
                                        <Chip size="small" variant="outlined" label={formatRemaining(Number(rubyOffer.expiresAt) - Number(now))} />
                                        {(rubyOffer.details || []).map(detail => <Chip key={detail} size="small" variant="outlined" label={t(detail)} />)}
                                    </Stack>
                                </Box>
                                <IconButton size="small" aria-label={expanded ? t('Collapse offer') : t('Expand offer')} onClick={() => toggleExpanded(key)}>
                                    <ExpandMoreRoundedIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }} />
                                </IconButton>
                            </Stack>

                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                            {rubyOffer.options?.length ? (
                                <Stack spacing={0.7} sx={{ mt: 0.85 }}>
                                    {rubyOffer.options.map(option => (
                                        <Paper key={option.key || option.packageID} variant="outlined" sx={{ p: 0.8, borderColor: 'rgba(255,255,255,.1)', background: 'rgba(2,6,23,.32)' }}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.7} justifyContent="space-between">
                                                <Stack direction="row" spacing={0.7} alignItems="center">
                                                    {option.iconUrl ? <Box component="img" src={option.iconUrl} alt="" sx={{ width: 38, height: 38, objectFit: 'contain' }} /> : <Inventory2RoundedIcon sx={{ color: '#facc15' }} />}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 900 }}>{t(option.title || `Package ${option.packageID}`)}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block', color: '#fde68a', fontWeight: 850 }}>
                                                            {option.affectedItem && !option.costs?.length ? t('Included in this discount') : `${t('Price')}: ${formatPrice(option)}`}
                                                            {option.regularCosts?.length ? ` (${t('regular')}: ${option.regularCosts.map(cost => `${formatter.format(Number(cost.amount || 0))} ${cost.currency}`).join(' + ')})` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Chip size="small" label={`#${option.packageID || option.itemID}`} variant="outlined" />
                                            </Stack>
                                            {option.rewards?.length ? (
                                                <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
                                                    {option.rewards.map(reward => <RewardItem key={reward.key || `${reward.type}-${reward.itemID}`} reward={reward} t={t} />)}
                                                </Stack>
                                            ) : null}
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="caption" sx={{ mt: 0.65, display: 'block', color: 'text.secondary' }}>
                                    {t('This is a live ruby discount applied directly in the related game shop.')}
                                </Typography>
                            )}
                            </Collapse>
                        </Paper>
                        )
                    })}
                </Stack>
            ) : null}

            <Divider sx={{ my: 1, borderColor: 'rgba(250,204,21,.14)' }} />

            {offers.length > 0 ? (
                <Stack spacing={1}>
                    {offers.flatMap(offer => (Array.isArray(offer.options) ? offer.options : []).map(option => {
                        const key = `${offer.offerID}:${Number(option.descriptionIndex ?? 0)}`
                        const expandKey = `private:${key}`
                        const expanded = isExpanded(expandKey)
                        const pending = requestedKey === key || (purchase?.state === 'pending' && `${purchase.offerID}:${Number(purchase.descriptionIndex ?? 0)}` === key)
                        const expired = !offer.infinite && Number(offer.expiresAt ?? 0) <= Number(now)
                        return (
                            <Paper key={key} variant="outlined" sx={{ p: 1, borderColor: 'rgba(255,255,255,.1)', background: 'rgba(2,6,23,.3)' }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                                    <Stack direction="row" spacing={0.8} sx={{ minWidth: 0 }}>
                                        {option.iconUrl ? (
                                            <Box component="img" src={option.iconUrl} alt="" sx={{ width: 46, height: 46, objectFit: 'contain', flexShrink: 0 }} />
                                        ) : (
                                            <LocalOfferRoundedIcon sx={{ width: 42, height: 42, color: '#facc15', flexShrink: 0 }} />
                                        )}
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 900, overflowWrap: 'anywhere' }}>
                                                {t(option.title || `Offer ${offer.offerID}`)}
                                            </Typography>
                                            <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap" sx={{ mt: 0.4 }}>
                                                <Chip size="small" label={`${t('Price')}: ${formatPrice(option)}`} sx={{ color: option.realMoney ? '#fca5a5' : '#fde68a' }} />
                                                <Chip size="small" variant="outlined" label={offer.infinite ? t('No time limit') : formatRemaining(Number(offer.expiresAt) - Number(now))} />
                                                <Chip size="small" variant="outlined" label={t(offer.state || 'Available')} />
                                            </Stack>
                                        </Box>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Tooltip title={option.realMoney ? t('Real-money offers are display-only and cannot be purchased by the bot.') : !option.purchasable ? t('This offer is not currently purchasable.') : t('A confirmation is required before purchase.')}>
                                        <span>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={pending ? <CircularProgress size={15} color="inherit" /> : <ShoppingCartCheckoutRoundedIcon />}
                                                disabled={pending || expired || !option.purchasable || option.realMoney || !ws || Number(ws.readyState) !== 1}
                                                onClick={() => requestPurchase(offer, option)}
                                                sx={{ minWidth: 132, backgroundColor: '#ca8a04', '&:hover': { backgroundColor: '#a16207' } }}
                                            >
                                                {pending ? t('Purchasing...') : t('Buy offer')}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <IconButton size="small" aria-label={expanded ? t('Collapse offer') : t('Expand offer')} onClick={() => toggleExpanded(expandKey)}>
                                        <ExpandMoreRoundedIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }} />
                                    </IconButton>
                                    </Stack>
                                </Stack>

                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Typography variant="caption" sx={{ mt: 0.9, mb: 0.55, display: 'block', color: 'text.secondary', fontWeight: 800 }}>
                                    {t('Contents')}
                                </Typography>
                                {option.rewards?.length ? (
                                    <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap">
                                        {option.rewards.map(reward => <RewardItem key={reward.key || `${reward.type}-${reward.itemID}`} reward={reward} t={t} />)}
                                    </Stack>
                                ) : (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('The game did not include item details for this offer.')}
                                    </Typography>
                                )}
                                </Collapse>
                            </Paper>
                        )
                    }))}
                </Stack>
            ) : rubyOffers.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('No live offers have been reported yet. They will appear here automatically when the game publishes them.')}
                </Typography>
            ) : null}
        </Box>
    )
}
